import React from 'react'
import { ChargingCardsTable } from './ChargingCardsTable'
import { useFetchChargingCards } from '../api/use-fetch-charging-cards'
import { ApiTableSkeleton } from '@e3dc-react/shell/Elements/Skeletons'
import { useCustomTranslation } from '@e3dc-react/shell/Hooks/useCustomTranslation'
import { ErrorMessage } from '@e3dc-react/shell/Elements/Error'
import { Stack, useMediaQuery, useTheme } from '@mui/material'
import { BannerLocations, BannerQueue } from 'src/Libs/banners'
import { useHagerBridgePermissions } from '../../shared'
import { ChargingCardToWallboxLinkDialogButton } from 'src/Libs/e-mobility/charging-cards/components/link-wallboxes/ChargingCardToWallboxLinkDialogButton'
import { useTypedSelector } from 'src/State/RootReducer'
import { useDispatch } from 'react-redux'
import { updateChargingCardsMobileSort } from 'src/State/E-Mobility/EMobilityStateAction'
import { SortMenu } from '@e3dc-react/shell/Elements/Table'
import { SortOption } from '@e3dc-react/shell/Elements/Sort'
import { ChargingCardModel } from '../api/models/charging-card-model'

interface ChargingCardsProps {
  installationId: number
}

/** Component shows charging cards table overview */
export const ChargingCards: React.FC<ChargingCardsProps> = ({ installationId }) => {
  const { chargingCards, wallboxes, isLoading, isError } = useFetchChargingCards(installationId)
  const { t } = useCustomTranslation()
  const { permissions } = useHagerBridgePermissions()

  const theme = useTheme()
  const isTabletOrMobile = useMediaQuery(theme.breakpoints.down('md'))

  const chargingCadsMobileSort = useTypedSelector(state => state.eMobility.chargingCardsMobileSort.sort)
  const dispatch = useDispatch()

  if (isLoading) {
    return <ApiTableSkeleton withFilter={false} />
  }

  if (isError) {
    return <ErrorMessage title={t('emobility.chargingCards.errorMessage')} />
  }

  const mobileSortableColumns: SortOption<ChargingCardModel>[] = [
    { value: 'tokenId', label: t('installation.rfidCardEditor.badgeId') },
    { value: 'cardName', label: t('emobility.chargingCards.cardName') },
    { value: 'user', label: t('emobility.chargingCards.edit.user') },
    { value: 'status', label: t('emobility.chargingCards.status.status') },
    { value: 'changedAt', label: t('emobility.chargingCards.changed') },
    { value: 'linkedWallboxes', label: t('emobility.chargingCards.works-on') },
  ]

  const installationHasWallboxes = wallboxes.length > 0

  return (
    <>
      {permissions.tourGuidePermissions.viewTourGuide && <BannerQueue location={BannerLocations.EmobilityTour} />}

      <Stack>
        <Stack direction={'row'} justifyContent={isTabletOrMobile ? 'space-between' : 'flex-end'} spacing={2} mb={2}>
          {permissions.chargingCardPermissions.linkChargingCards && (
            <ChargingCardToWallboxLinkDialogButton
              installationId={installationId}
              chargingCards={chargingCards}
              installationHasWallboxes={installationHasWallboxes}
            />
          )}

          {isTabletOrMobile && (
            <SortMenu
              sortableColumns={mobileSortableColumns}
              value={{ name: chargingCadsMobileSort?.name ?? 'tokenId', direction: chargingCadsMobileSort?.direction ?? 'asc' }}
              onChange={sort => dispatch(updateChargingCardsMobileSort(sort))}
            />
          )}
        </Stack>

        <ChargingCardsTable chargingCards={chargingCards} installationId={installationId} installationHasWallboxes={installationHasWallboxes} />
      </Stack>
    </>
  )
}
